import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import type { PublicUser } from '@messe/shared'
import { passwordSchema } from '@messe/shared'
import { Button } from './ui/button.js'
import { CheckboxField, TextField } from './ui/field.js'
import { Alert } from './ui/feedback.js'
import { Dialog } from './ui/dialog.js'
import { format, t } from '../i18n/index.js'
import { ApiError } from '../lib/api.js'
import { generatePassphrase, resetUserPassword } from '../lib/users.js'

interface Props {
  open: boolean
  user: PublicUser | null
  onClose: () => void
}

export function PasswordResetDialog({ open, user, onClose }: Props) {
  const strings = t()

  const [password, setPassword] = useState('')
  const [revokeSessions, setRevokeSessions] = useState(true)
  const [fieldError, setFieldError] = useState<string | undefined>(undefined)
  const [formError, setFormError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!open) return
    setPassword(generatePassphrase())
    setRevokeSessions(true)
    setFieldError(undefined)
    setFormError(null)
    setDone(false)
    setCopied(false)
  }, [open, user])

  const reset = useMutation({
    mutationFn: () => {
      if (!user) throw new Error('No user selected')
      return resetUserPassword(user.id, password, revokeSessions)
    },
    onSuccess: () => setDone(true),
    onError: (error) => {
      setFormError(
        error instanceof ApiError
          ? (error.problem.detail ?? strings.errors.unexpected)
          : strings.errors.unexpected,
      )
    },
  })

  async function copyPassword() {
    try {
      await navigator.clipboard.writeText(password)
      setCopied(true)
    } catch {
      // Clipboard access can be denied; the password is on screen either way.
      setCopied(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={strings.users.resetPassword.heading}
      description={
        user ? format(strings.users.resetPassword.description, { name: user.name }) : undefined
      }
      footer={
        done ? (
          <Button onClick={onClose}>{strings.common.close}</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose}>
              {strings.users.actions.cancel}
            </Button>
            <Button
              disabled={reset.isPending}
              onClick={() => {
                setFormError(null)
                const parsed = passwordSchema.safeParse(password)
                if (!parsed.success) {
                  setFieldError(parsed.error.issues[0]?.message)
                  return
                }
                setFieldError(undefined)
                reset.mutate()
              }}
            >
              {reset.isPending ? strings.users.actions.saving : strings.users.resetPassword.submit}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        {formError && <Alert tone="danger">{formError}</Alert>}

        {done ? (
          <>
            <Alert tone="success">{strings.users.resetPassword.success}</Alert>
            <Alert tone="warning">{strings.users.resetPassword.copyHint}</Alert>
            <div className="flex items-center gap-2">
              <code className="bg-surface-sunken flex-1 rounded-md px-3 py-2 font-mono text-sm">
                {password}
              </code>
              <Button variant="secondary" onClick={() => void copyPassword()}>
                {copied ? strings.users.resetPassword.copied : strings.users.resetPassword.copy}
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <TextField
                  label={strings.users.resetPassword.newPassword}
                  hint={strings.users.create.passwordHint}
                  autoComplete="new-password"
                  value={password}
                  error={fieldError}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>
              <Button
                variant="secondary"
                className="mb-6"
                onClick={() => setPassword(generatePassphrase())}
              >
                {strings.users.create.generate}
              </Button>
            </div>

            <CheckboxField
              label={strings.users.resetPassword.revokeSessions}
              hint={strings.users.resetPassword.revokeHint}
              checked={revokeSessions}
              onChange={(event) => setRevokeSessions(event.target.checked)}
            />
          </>
        )}
      </div>
    </Dialog>
  )
}
